// src/pages/categories/components/CategoryItem/CategoryItem.tsx

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CategoryInterface } from "../../../../common/types/types";
import { getImageUri } from "../../../../common/utils/functions/getImageUri";
import styles from "./CategoryItem.module.scss";

type Props = {
  category: CategoryInterface;
};

const MOBILE_BREAKPOINT = 768;
const CHILDREN_LIMIT_DESKTOP = 3;
const CHILDREN_LIMIT_MOBILE = 2;

function useChildrenLimit() {
  const [limit, setLimit] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
      ? CHILDREN_LIMIT_MOBILE
      : CHILDREN_LIMIT_DESKTOP,
  );

  useEffect(() => {
    const onResize = () => {
      setLimit(
        window.innerWidth <= MOBILE_BREAKPOINT
          ? CHILDREN_LIMIT_MOBILE
          : CHILDREN_LIMIT_DESKTOP,
      );
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return limit;
}

function buildChildrenLine(children: CategoryInterface[], limit: number) {
  if (children.length === 0) return { text: "", hidden: 0 };

  const visible = children.slice(0, limit);
  const hidden = children.length - visible.length;

  return {
    text: visible.map((c) => c.name).join(", "),
    hidden,
  };
}

export default function CategoryItem({ category }: Props) {
  const limit = useChildrenLimit();
  const children = category.children ?? [];
  const { text: childrenText, hidden: hiddenCount } = buildChildrenLine(
    children,
    limit,
  );

  const imageFile = category.files?.[0];

  return (
    <Link
      to={`/category/${category.id}`}
      className={styles.container}
      aria-label={`Перейти в категорию ${category.name}`}
    >
      {imageFile ? (
        <img
          src={`${getImageUri()}${imageFile}`}
          alt=""
          aria-hidden="true"
          className={styles.image}
        />
      ) : (
        <div className={styles.imagePlaceholder} aria-hidden="true">
          <span className={styles.placeholderText}>Нет фото</span>
        </div>
      )}

      <div className={styles.contentBlock}>
        <span className={styles.name}>{category.name}</span>

        <p className={styles.description}>{category.description ?? ""}</p>

        <div className={styles.childrenLine}>
          <span className={styles.childrenText}>{childrenText}</span>
          {hiddenCount > 0 && (
            <span className={styles.more}>+{hiddenCount}</span>
          )}
        </div>
      </div>
    </Link>
  );
}